/**
 * QC Violations — hub shell.
 *
 * Single route for everything QC. Three sub-tabs:
 *   1. Queue           — HR review queue (pending / approved / rejected / removed)
 *   2. Link Backfill   — canonical employee_id backfill tool (moved from Admin Maintenance)
 *   3. Access Control  — Super Admin: per-role on/off toggles (backend-enforced)
 *
 * The employee-profile QC tab (EmployeeQCViolationsTab) lives on the
 * Employee Details page and is intentionally NOT part of this hub — that's
 * per-employee context, not global QC administration.
 *
 * The ops integration endpoints (`/api/integration/qc-violations*`) are
 * outside the scope of this hub and are NOT affected by the access toggle.
 */
import React, { useState } from 'react';
import { AlertTriangle, ShieldCheck, Database, Inbox } from 'lucide-react';

import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';

import QCQueueTab from './QCQueueTab';
import QCLinkBackfillTab from './QCLinkBackfillTab';
import QCAccessControlTab from './QCAccessControlTab';

const QCViolationsPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRTL = language === 'ar';
  const t = (en, ar) => (isRTL ? ar : en);
  const isSuperAdmin = user?.role === 'super_admin';

  const [tab, setTab] = useState('queue');

  return (
    <div className="p-6 space-y-6" dir={isRTL ? 'rtl' : 'ltr'} data-testid="qc-violations-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-amber-600" />
          {t('QC Violations', 'مخالفات ضبط الجودة')}
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          {t(
            'The single place for everything QC — HR review queue, link backfill, and access control. Ops-side data ingestion is not administered here (that runs on the service-token integration endpoint).',
            'مركز واحد لكل ما يتعلق بضبط الجودة — طابور المراجعة، تعبئة روابط السجلات، وإدارة صلاحيات الوصول. لا تُدار عملية استيراد البيانات من طرف العمليات هنا (تعمل عبر نقطة تكامل الرمز الخدمي).'
          )}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="qc-hub-tabs">
          <TabsTrigger value="queue" data-testid="qc-hub-tab-queue" className="gap-2">
            <Inbox className="h-4 w-4" />
            {t('Queue', 'الطابور')}
          </TabsTrigger>
          <TabsTrigger value="backfill" data-testid="qc-hub-tab-backfill" className="gap-2">
            <Database className="h-4 w-4" />
            {t('Link Backfill', 'تعبئة الروابط')}
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="access" data-testid="qc-hub-tab-access" className="gap-2">
              <ShieldCheck className="h-4 w-4" />
              {t('Access Control', 'إدارة الصلاحيات')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="queue" className="mt-0">
          <QCQueueTab />
        </TabsContent>

        <TabsContent value="backfill" className="mt-0">
          <QCLinkBackfillTab />
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="access" className="mt-0">
            <QCAccessControlTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default QCViolationsPage;
